import { formatMoney } from '../lib/format.js'
import StatusPill from './StatusPill.jsx'
import { toneFor, labelFor } from '../lib/pillStatus.js'

// CateringInquiryRow — single catering inquiry row, extracted verbatim
// from CateringScreen's inline InquiryRow so the single-unit screen and
// the group-level pipeline render identical markup.
//
// Status-tinted, tappable. Compresses date + headcount + estimated total
// into one readable line. For non-completed inquiries, also surfaces a
// small "✨ Quote" button that opens the AI quote generator.
//
// GROUP EXTENSION: an optional `assignedUnit` prop ({ name, color })
// renders a small color-coded unit chip after the status pill. The
// single-unit screen passes nothing → no chip → byte-identical to today.
export default function CateringInquiryRow({ inquiry, onTap, onAiQuote, assignedUnit }) {
  const showAiButton =
    onAiQuote && ['new', 'quoted', 'confirmed'].includes(inquiry.status)

  // Friendly date — "Sat Apr 26" or "in 3 days" if very near
  const dateStr = inquiry.event_date
    ? new Date(inquiry.event_date + 'T00:00:00')
        .toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        })
    : null

  return (
    <li className="flex items-stretch border-b border-black/5 last:border-b-0">
      <button
        type="button"
        onClick={onTap}
        className="flex flex-1 items-start justify-between gap-3 px-4 py-3 text-left hover:bg-blue-50"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-base font-medium">
              {inquiry.contact_name}
            </span>
            <StatusPill tone={toneFor('catering', inquiry.status)}>
              {labelFor('catering', inquiry.status)}
            </StatusPill>
            {assignedUnit && (
              <span
                className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium"
                style={{
                  backgroundColor: assignedUnit.color?.bg,
                  color: assignedUnit.color?.text,
                }}
                title={`Assigned to ${assignedUnit.name}`}
              >
                <span
                  className="inline-block h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: assignedUnit.color?.dot }}
                  aria-hidden="true"
                />
                <span className="max-w-[10rem] truncate">{assignedUnit.name}</span>
              </span>
            )}
          </div>
          <div className="mt-0.5 truncate text-xs text-black/60">
            {[
              dateStr,
              inquiry.headcount ? `${inquiry.headcount} ppl` : null,
              inquiry.event_type,
              inquiry.location,
            ].filter(Boolean).join(' · ')}
            {!dateStr && !inquiry.headcount && !inquiry.event_type && !inquiry.location &&
              (inquiry.contact_phone || inquiry.contact_email || 'no event details yet')
            }
          </div>
        </div>
        {inquiry.estimated_total != null && Number(inquiry.estimated_total) > 0 && (
          <div className="text-right text-base font-medium tabular-nums text-emerald-700">
            {formatMoney(inquiry.estimated_total)}
          </div>
        )}
      </button>
      {showAiButton && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onAiQuote() }}
          className="min-h-btn shrink-0 border-l border-black/5 px-3 text-xs font-medium text-blue-700 hover:bg-blue-50"
          aria-label={`Generate AI quote for ${inquiry.contact_name}`}
          title="Generate AI quote"
        >
          ✨ Quote
        </button>
      )}
    </li>
  )
}
